import type { CSSProperties, ComponentPropsWithoutRef, FocusEvent, MouseEvent, ReactElement, ReactNode } from 'react'
import { cloneElement, forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react'

import { cn } from '@adverity/ds-tokens/cn'
import { createPortal } from 'react-dom'

import { useTooltipProvider } from '../tooltip-provider/tooltip-provider'

const DEFAULT_TOOLTIP_DELAY = 500

const focusEventsHandledByTooltip = new WeakSet<Event>()

/**
 * Tooltip using CSS Anchor Positioning and a portal.
 *
 * Browser Support: Chrome 125+, Edge 125+
 * Uses CSS Anchor Positioning API for positioning without JavaScript.
 *
 * @see https://developer.chrome.com/blog/anchor-positioning-api
 */

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

type TooltipProps = {
    /** The element that triggers the tooltip on hover/focus */
    children: ReactElement
    /** Content to display in the tooltip */
    content: ReactNode
    /** Placement of the tooltip relative to the trigger */
    placement?: TooltipPlacement
    /** Additional className for the tooltip content */
    className?: string
    /** Delay before showing tooltip in ms (CSS transition-delay) */
    delay?: number
    /** Controlled visibility override. `false` suppresses tooltip, `true` forces it visible. */
    visible?: boolean
}

type TooltipContentProps = ComponentPropsWithoutRef<'div'> & {
    anchorName: string
    placement: TooltipPlacement
    delay: number
    isVisible: boolean
}

/**
 * Anchor positioning styles per placement.
 * All positioning properties (insets, margins, cross-axis alignment, flip fallbacks) are
 * consolidated in inline styles so the browser's CSS Anchor Positioning engine can process
 * them together — required for `position-try-fallbacks` (flip-block/flip-inline) to work.
 *
 * @see https://developer.chrome.com/blog/anchor-positioning-api#primitives_and_position-try-fallbacks
 */
const placementPositionStyles: Record<TooltipPlacement, Record<string, string>> = {
    top: {
        inset: 'auto',
        bottom: 'anchor(top)',
        marginBottom: 'var(--spacing-x-small)',
        justifySelf: 'anchor-center',
        positionTryFallbacks: 'flip-block',
    },
    bottom: {
        inset: 'auto',
        top: 'anchor(bottom)',
        marginTop: 'var(--spacing-x-small)',
        justifySelf: 'anchor-center',
        positionTryFallbacks: 'flip-block',
    },
    left: {
        inset: 'auto',
        right: 'anchor(left)',
        marginRight: 'var(--spacing-x-small)',
        alignSelf: 'anchor-center',
        positionTryFallbacks: 'flip-inline',
    },
    right: {
        inset: 'auto',
        left: 'anchor(right)',
        marginLeft: 'var(--spacing-x-small)',
        alignSelf: 'anchor-center',
        positionTryFallbacks: 'flip-inline',
    },
}

const TooltipContent = forwardRef<HTMLDivElement, TooltipContentProps>(
    ({ anchorName, placement, delay, isVisible, className, children, ...props }, ref) => (
        <div
            ref={ref}
            role="tooltip"
            aria-hidden={!isVisible}
            className={cn(
                // Base styles
                'bg-brand-background-inverted text-text-inverted',
                'px-x-small py-xx-small',
                'rounded-medium text-[12px] leading-tight',
                'max-w-8x-large wrap-break-word',
                // Positioning
                'fixed',
                // Animation
                'pointer-events-none transition-opacity duration-150',
                isVisible ? 'opacity-100' : 'opacity-0',
                // Shadow
                'shadow-md',
                'z-tooltip',
                className,
            )}
            style={
                {
                    positionAnchor: `--${anchorName}`,
                    ...placementPositionStyles[placement],
                    transitionDelay: isVisible ? `${delay}ms` : '0ms',
                } as CSSProperties
            }
            {...props}
        >
            {children}
        </div>
    ),
)

TooltipContent.displayName = 'TooltipContent'

/**
 * Tooltip component using CSS Anchor Positioning and a portal.
 *
 * Features:
 * - CSS Anchor Positioning (no JavaScript positioning calculations)
 * - Accessible with ARIA attributes
 * - Keyboard accessible (shows on focus, dismiss with Escape)
 * - Only one tooltip visible at a time (via TooltipProvider)
 *
 * @example
 * ```tsx
 * <Tooltip content="Helpful information">
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 */
const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
    ({ children, content, placement = 'top', className, delay = DEFAULT_TOOLTIP_DELAY, visible }, ref) => {
        const id = useId()
        const anchorName = `tooltip-anchor-${id.replace(/:/g, '')}`
        const tooltipId = `tooltip-${id.replace(/:/g, '')}`

        const [hoverVisible, setHoverVisible] = useState(false)
        const hasBeenShownRef = useRef(false)
        const { register, requestOpen, notifyClose } = useTooltipProvider()

        useEffect(
            () =>
                register(id, {
                    show: () => setHoverVisible(true),
                    dismiss: () => setHoverVisible(false),
                }),
            [id, register],
        )

        const show = useCallback(() => {
            hasBeenShownRef.current = true
            requestOpen(id)
            setHoverVisible(true)
        }, [id, requestOpen])

        const hide = useCallback(() => {
            setHoverVisible(false)
            notifyClose(id)
        }, [id, notifyClose])

        // visible prop overrides hover/focus behavior: false = suppress, true = force, undefined = default
        const isVisible = visible ?? hoverVisible

        useEffect(() => {
            if (!isVisible) return

            const onKeyDown = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    hide()
                }
            }

            const onScroll = () => {
                hide()
            }

            const scrollOptions: AddEventListenerOptions = { capture: true, passive: true }

            document.addEventListener('keydown', onKeyDown, true)
            document.addEventListener('scroll', onScroll, scrollOptions)

            return () => {
                document.removeEventListener('keydown', onKeyDown, true)
                document.removeEventListener('scroll', onScroll, scrollOptions)
            }
        }, [isVisible, hide])

        // Disabled elements can't receive mouse events, so wrap them in a span
        const isChildDisabled = Boolean(children.props.disabled)

        const trigger = isChildDisabled ? (
            <span
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={(event) => {
                    if (!focusEventsHandledByTooltip.has(event.nativeEvent)) {
                        show()
                        focusEventsHandledByTooltip.add(event.nativeEvent)
                    }
                }}
                onBlur={hide}
                style={{ anchorName: `--${anchorName}`, display: 'inline-block' } as CSSProperties}
            >
                {/* eslint-disable-next-line @eslint-react/no-clone-element */}
                {cloneElement(children, { 'aria-describedby': tooltipId })}
            </span>
        ) : (
            // Clone the child element to add aria-describedby and event handlers directly
            // onMouseEnter (not onMouseOver) prevents bubbling so nested tooltips work correctly
            // eslint-disable-next-line @eslint-react/no-clone-element
            cloneElement(children, {
                'aria-describedby': tooltipId,
                onMouseEnter: (event: MouseEvent) => {
                    show()
                    children.props.onMouseEnter?.(event)
                },
                onMouseLeave: (event: MouseEvent) => {
                    hide()
                    children.props.onMouseLeave?.(event)
                },
                onFocus: (event: FocusEvent) => {
                    if (!focusEventsHandledByTooltip.has(event.nativeEvent)) {
                        show()
                        focusEventsHandledByTooltip.add(event.nativeEvent)
                    }
                    children.props.onFocus?.(event)
                },
                onBlur: (event: FocusEvent) => {
                    hide()
                    children.props.onBlur?.(event)
                },
                style: {
                    ...children.props.style,
                    anchorName: `--${anchorName}`,
                } as CSSProperties,
            })
        )

        return (
            <>
                {trigger}
                {(hasBeenShownRef.current || isVisible) &&
                    createPortal(
                        <TooltipContent
                            ref={ref}
                            id={tooltipId}
                            anchorName={anchorName}
                            placement={placement}
                            delay={delay}
                            isVisible={isVisible}
                            className={className}
                        >
                            {content}
                        </TooltipContent>,
                        document.body,
                    )}
            </>
        )
    },
)

Tooltip.displayName = 'Tooltip'

export { Tooltip }
